/**
 * DRAWING THE CURLY ARROWS ONTO THE MOLECULE.
 *
 * ── THE COORDINATES COME OUT OF THE DRAWING ITSELF ────────────────────────────────────────────
 * OpenChemLib puts an invisible hit target on every atom and every bond —
 * `<circle id="m:Atom:3" cx cy r="8" opacity="0">` and `<line id="m:Bond:1" x1 y1 x2 y2>` — so
 * where each one SITS can be read straight off the rendered SVG. That keeps this a pure
 * function of a string: no DOM, no measuring, and the same answer in a test as on a page.
 *
 * It also means the arrows are in the drawing's own coordinate space, so they survive the
 * molecule being drawn at any size — the viewBox scales both together.
 *
 * ── WHY A BOW AND NOT A STRAIGHT LINE ─────────────────────────────────────────────────────────
 * A curly arrow is curved because it has to pass OVER the structure without being read as a
 * bond. A straight arrow between two atoms is a bond with a point on it.
 */

import { pruneArrows, type Anchor, type Arrow } from './mechanism-arrows';

export type Point = { x: number; y: number };

/** The drawing's own coordinate window, which is what turns a position into a fraction. */
export function viewBoxOf(svg: string): { x: number; y: number; w: number; h: number } | null {
  const m = /viewBox="([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)"/.exec(svg);
  if (!m) return null;
  const [, x, y, w, h] = m.map(Number) as unknown as [string, number, number, number, number];
  return w > 0 && h > 0 ? { x, y, w, h } : null;
}

/**
 * GROW THE WINDOW UNTIL EVERYTHING DRAWN ON THE MOLECULE IS INSIDE IT.
 *
 * OpenChemLib crops the viewBox to the molecule, and every mark we add afterwards sits OUTSIDE
 * an atom — a number is set up and to the right of one, a lone pair sticks out at 1.45 units, a
 * curly arrow bows away from the bond it leaves. All of it was being sliced by the edge of a box
 * drawn before any of it existed. Measured on the old behaviour: 3 of 6 numbers on benzene fell
 * outside the box, 5 of 10 on naphthalene, 2 of 3 on ethanol, and 5 of 6 index pins on benzene.
 * The drawing looked right in the editor and published with its marks cut off.
 *
 * Each overlay reports the extremes of what it drew rather than everything being padded by a
 * fixed margin: a margin big enough for a three-character number is most of a small molecule's
 * width, and benzene would have lost a third of its size to room nothing was using.
 *
 * The width and height ATTRIBUTES are left alone, so the drawing keeps the same footprint on
 * screen and the molecule inside it gets a little smaller. That is the trade, and it is the
 * right way round: a molecule 5% smaller beats a number that is not there.
 */
export function includeInViewBox(svg: string, pts: Point[]): string {
  const box = viewBoxOf(svg);
  if (!box || !pts.length) return svg;
  let left = box.x;
  let top = box.y;
  let right = box.x + box.w;
  let bottom = box.y + box.h;
  for (const p of pts) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    left = Math.min(left, p.x);
    top = Math.min(top, p.y);
    right = Math.max(right, p.x);
    bottom = Math.max(bottom, p.y);
  }
  if (left === box.x && top === box.y && right === box.x + box.w && bottom === box.y + box.h) {
    return svg;
  }
  /*
   * ROUNDED OUTWARD, not to the nearest.
   *
   * The box is written to two decimals, and `toFixed` rounds to the nearest — so an edge
   * landing on x.xx5 is written a hundredth INSIDE the mark it was grown for, and the mark is
   * clipped by exactly that hundredth. Measured on benzene with a three-character mark: the
   * right edge came out at 169.69 for a corner at 169.70.
   *
   * A hundredth of a unit is invisible and the rounding is still the right idea; what has to
   * change is the direction. Floor the near edges and ceil the far ones and the written box
   * always contains what it was asked to contain.
   */
  const floor2 = (v: number) => Math.floor(v * 100) / 100;
  const ceil2 = (v: number) => Math.ceil(v * 100) / 100;
  const x0 = floor2(left);
  const y0 = floor2(top);
  const w = ceil2(right) - x0;
  const h = ceil2(bottom) - y0;
  const grown = svg.replace(/viewBox="[^"]*"/,
    `viewBox="${x0.toFixed(2)} ${y0.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)}"`);

  /*
   * AND THE DRAWING GROWS WITH ITS WINDOW, rather than the molecule shrinking inside it.
   *
   * The SVG carries its natural size in pixels — see `STRUCTURE_PX_PER_UNIT` — and that size is
   * what makes one unit worth the same pixel on every molecule. Widening the window without
   * widening the picture would letterbox the molecule into the old box, so adding a lone pair
   * would visibly shrink the structure it was added to.
   *
   * Scaled by the same ratio the box grew by, which keeps units-to-pixels exactly where it was.
   */
  return grown.replace(/^(<svg)([^>]*?)(\s*\/?>)/, (all: string, open: string, attrs: string, close: string) => {
    const aw = /\swidth="([\d.]+)"/.exec(attrs);
    const ah = /\sheight="([\d.]+)"/.exec(attrs);
    if (!aw || !ah) return all;
    const px = Number(aw[1]) / box.w;
    return `${open}${attrs.replace(/\s(?:width|height)="[^"]*"/g, '')}`
      + ` width="${(w * px).toFixed(1)}" height="${(h * px).toFixed(1)}"${close}`;
  });
}

/**
 * How far either side of its anchor a run of text reaches, for `includeInViewBox`.
 *
 * `text-anchor="middle"`, so it is half the run each way; a digit is about 0.55em and the
 * estimate is deliberately generous, because the cost of over-reserving is a slightly smaller
 * molecule and the cost of under-reserving is the thing this is here to stop.
 */
export const textExtent = (text: string, fontSize: number) => ({
  half: text.length * fontSize * 0.33,
  above: fontSize * 0.85,
  below: fontSize * 0.25,
});

const num = (s: string | undefined) => Number(s ?? NaN);

/**
 * Where every atom and bond sits, read off the rendered SVG.
 *
 * A bond's point is the middle of its line, which is where an arrow to or from a bond belongs —
 * an arrow aimed at one end of a double bond reads as aimed at that atom.
 */
export function anchorPoints(svg: string): { atoms: Point[]; bonds: Point[] } {
  const atoms: Point[] = [];
  const bonds: Point[] = [];

  for (const m of svg.matchAll(/<circle id="[^":]+:Atom:(\d+)"[^>]*cx="([-\d.]+)"[^>]*cy="([-\d.]+)"/g)) {
    atoms[Number(m[1])] = { x: num(m[2]), y: num(m[3]) };
  }
  for (const m of svg.matchAll(
    /<line id="[^":]+:Bond:(\d+)"[^>]*x1="([-\d.]+)"[^>]*y1="([-\d.]+)"[^>]*x2="([-\d.]+)"[^>]*y2="([-\d.]+)"/g)) {
    bonds[Number(m[1])] = { x: (num(m[2]) + num(m[4])) / 2, y: (num(m[3]) + num(m[5])) / 2 };
  }
  return { atoms, bonds };
}

const pointFor = (a: Anchor, at: { atoms: Point[]; bonds: Point[] }): Point | undefined =>
  (a.kind === 'atom' ? at.atoms : at.bonds)[a.index];

/**
 * The library's own bond length, which it draws the same on every molecule.
 *
 * Measured off the drawing rather than assumed, by taking each bond's midpoint and doubling
 * its distance to the nearest atom; the median, so a double bond's offset line or a stray
 * anchor cannot move it. Five molecules from ethanol to naphthalene all came back at exactly
 * 24, which is what makes it the right ruler.
 */
const LIBRARY_BOND = 24;

export function bondLength(at: { atoms: Point[]; bonds: Point[] }): number {
  const atoms = at.atoms.filter(Boolean);
  const mids = at.bonds.filter(Boolean);
  if (!atoms.length || !mids.length) return LIBRARY_BOND;
  const lengths = mids
    .map(b => 2 * Math.min(...atoms.map(a => Math.hypot(a.x - b.x, a.y - b.y))))
    .sort((a, b) => a - b);
  return lengths[Math.floor(lengths.length / 2)] || LIBRARY_BOND;
}

/**
 * HOW BIG ANYTHING DRAWN ONTO A MOLECULE SHOULD BE, in the drawing's own units.
 *
 * ── IT WAS A FRACTION OF THE MOLECULE'S SPREAD, AND THAT IS THE WRONG RULER ──────────────────
 * An SVG unit means nothing on its own, so this took the distance across the whole molecule as
 * its reference. Wider molecules then got bigger marks — which is backwards, because what a
 * mark sits beside is one ATOM, and an atom is the same size on every drawing.
 *
 * Measured: the spread heuristic ran from 3.0 units on ethanol to its 9.0 ceiling on
 * bicyclohexyl, a factor of three, while the bond length under them never moved off 24. So a
 * teacher's number landed 19% of a bond from its vertex on ethanol and 56% of a bond away on
 * bicyclohexyl — *"the labels are way off the vertex"* — and its type grew from 5.5 units to
 * 16.2, which is larger than the element symbols it sits beside.
 *
 * The bond is the ruler. It is constant, it is local to the atom, and it is what a chemist
 * reads everything else against.
 *
 * Shared, because every overlay needs it and three copies of a heuristic drift: the pins, the
 * Lewis dots and the teacher's own numbers all have to agree about how big an atom is.
 */
export function drawUnit(at: { atoms: Point[]; bonds: Point[] }): number {
  return bondLength(at) * 0.26;
}

/**
 * WHICH ATOMS EACH BOND JOINS, which the drawing does not say outright.
 *
 * `anchorPoints` gives atom positions and bond MIDPOINTS, and a midpoint is by definition
 * equidistant from the two atoms it lies between — so the two nearest atoms to a midpoint are
 * its ends. That is enough connectivity to answer the only question the overlays ask of it:
 * which directions are already taken at this atom.
 */
export function bondEnds(at: { atoms: Point[]; bonds: Point[] }): [number, number][] {
  const out: [number, number][] = [];
  for (const mid of at.bonds) {
    if (!mid) continue;
    const ranked = at.atoms
      .map((a, i) => ({ i, d: a ? Math.hypot(a.x - mid.x, a.y - mid.y) : Infinity }))
      .sort((x, y) => x.d - y.d);
    const [first, second] = ranked;
    if (first && second && Number.isFinite(second.d)) out.push([first.i, second.i]);
  }
  return out;
}

/**
 * THE EMPTIEST DIRECTION AROUND AN ATOM, as an angle in radians.
 *
 * A mark beside an atom used to go up and to the right, always. That works where the atom
 * happens to be free up and to the right and nowhere else: on a ring it lands ON a bond, and
 * on a crowded skeleton it lands nearer the NEXT vertex than its own, so it reads as that
 * atom's mark instead. Reported from the real panel — *"this default location results in
 * overlap with the structure or sometimes misalignment with the wrong vertex"*.
 *
 * So: take the directions of the bonds leaving this atom, find the widest gap between two
 * neighbouring ones, and point at the middle of it. That is the most room the atom has, and it
 * is what a chemist does by hand. An atom with no bonds at all has nothing to avoid and keeps
 * the old corner.
 */
export function freeDirection(
  at: { atoms: Point[]; bonds: Point[] }, index: number, ends?: [number, number][],
): number {
  const atom = at.atoms[index];
  if (!atom) return -Math.PI / 4;
  const taken: number[] = [];
  for (const [a, b] of ends ?? bondEnds(at)) {
    const other = a === index ? b : b === index ? a : -1;
    if (other < 0) continue;
    const p = at.atoms[other];
    if (p) taken.push(Math.atan2(p.y - atom.y, p.x - atom.x));
  }
  /* Up and to the right, which is where a substituent is least often already drawn. */
  if (!taken.length) return -Math.PI / 4;
  if (taken.length === 1) return taken[0]! + Math.PI;

  taken.sort((x, y) => x - y);
  let best = taken[0]! + Math.PI;
  let widest = -1;
  for (let i = 0; i < taken.length; i++) {
    const from = taken[i]!;
    const to = i + 1 < taken.length ? taken[i + 1]! : taken[0]! + Math.PI * 2;
    const gap = to - from;
    if (gap > widest) { widest = gap; best = from + gap / 2; }
  }
  return best;
}

/**
 * Pulled back off the anchor, so the head does not sit on top of an atom's letter.
 *
 * PROPORTIONAL, not a fixed number of pixels. A nucleophile attacking the carbon one bond away
 * is the commonest arrow there is, and at a fixed 9px each end almost nothing of a 24px arrow
 * was left — it drew as a tight curl with an oversized head wedged between two atoms.
 */
function shortened(from: Point, to: Point): [Point, Point] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const back = Math.min(8, len * 0.2);
  return [
    { x: from.x + ux * back, y: from.y + uy * back },
    { x: to.x - ux * back, y: to.y - uy * back },
  ];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * One arrow: a bowed path, and a head that says how many electrons moved.
 *
 * A full head is a pair and a half head is one — heterolysis against homolysis. They are
 * different mechanisms rather than two styles, so the half head really is half: one barb.
 */
function oneArrow(
  from: Point, to: Point, head: Arrow['head'], flip: boolean,
): { markup: string; pts: Point[] } {
  const [a, b] = shortened(from, to);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  /* The control point, pushed out along the normal: this is the curl. Proportional, so a short
     arrow curls a little and a long one sweeps — a fixed bow turns a short arrow into a loop.
     `flip` puts it on the other side, for two arrows leaving the same place. */
  const bow = clamp(len * 0.4, 4, 24) * (flip ? -1 : 1);
  const cx = (a.x + b.x) / 2 - (dy / len) * bow;
  const cy = (a.y + b.y) / 2 + (dx / len) * bow;

  /* The head points along the path's own last leg — the tangent at the end — not along the
     straight line between the ends, or it leans off a strongly bowed arrow. */
  const angle = Math.atan2(b.y - cy, b.x - cx);
  const size = clamp(len * 0.3, 3.5, 6.5);
  const tip = `${b.x},${b.y}`;
  const left = `${b.x - Math.cos(angle - 0.45) * size},${b.y - Math.sin(angle - 0.45) * size}`;
  const right = `${b.x - Math.cos(angle + 0.45) * size},${b.y - Math.sin(angle + 0.45) * size}`;

  const barbs = head === 'pair' ? `${tip} ${left} ${right}` : `${tip} ${left}`;
  /*
   * The curve's hull, not the curve. A quadratic Bezier never leaves the triangle of its two
   * ends and its control point, so those three contain it — generously, since the curve reaches
   * only halfway to the control point, and generous is the right side to be wrong on here.
   */
  const corner = (ang: number) => ({
    x: b.x - Math.cos(ang) * size, y: b.y - Math.sin(ang) * size,
  });
  return {
    markup: `<path class="mx-arrow" d="M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}" fill="none"/>`
      + `<polygon class="mx-head" points="${barbs}"/>`,
    pts: [a, b, { x: cx, y: cy }, corner(angle - 0.45), corner(angle + 0.45)],
  };
}

/**
 * The arrows as an SVG fragment, to sit inside the molecule's own `<svg>`.
 *
 * Returns empty when nothing can be drawn, so the caller can leave the molecule untouched
 * rather than wrapping it in an empty group.
 */
function arrowsParts(svg: string, arrows: Arrow[]): { markup: string; edges: Point[] } {
  const at = anchorPoints(svg);
  const live = pruneArrows(arrows, at.atoms.length, at.bonds.length);

  /* Two arrows leaving the SAME place curl opposite ways. That is homolysis: one electron goes
     each way, and both bowed the same side drew them crossing each other. */
  const seen = new Map<string, number>();

  const edges: Point[] = [];
  const drawn = live
    .map(a => {
      const from = pointFor(a.from, at);
      const to = pointFor(a.to, at);
      /* A gap in the array — an index the drawing has no element for — is the same as a missing
         one. `pruneArrows` catches the out-of-range case; this catches the hole. */
      if (!from || !to) return '';
      const key = `${a.from.kind}${a.from.index}`;
      const nth = seen.get(key) ?? 0;
      seen.set(key, nth + 1);
      const one = oneArrow(from, to, a.head, nth % 2 === 1);
      edges.push(...one.pts);
      return one.markup;
    })
    .filter(Boolean);

  return { markup: drawn.length ? `<g class="mx">${drawn.join('')}</g>` : '', edges };
}

/** The arrows alone, for a caller placing them itself. The box is `withArrows`' business. */
export function arrowsOverlay(svg: string, arrows: Arrow[]): string {
  return arrowsParts(svg, arrows).markup;
}

/** The molecule with its arrows over it. Untouched when there are none to draw. */
export function withArrows(svg: string, arrows: Arrow[]): string {
  const { markup, edges } = arrowsParts(svg, arrows);
  if (!markup) return svg;
  const close = svg.lastIndexOf('</svg>');
  if (close < 0) return svg;
  /* A curl leaves the bond it starts from, so it leaves the box cropped to the molecule too. */
  return includeInViewBox(svg.slice(0, close) + markup + svg.slice(close), edges);
}

/**
 * THE SAME DRAWING WITH ITS ATOMS AND BONDS NUMBERED, for choosing arrow ends.
 *
 * An arrow is picked from two dropdowns reading "atom 3" and "bond 1", and those numbers mean
 * nothing unless the teacher can see WHERE they are. Clicking the molecule is the faster way and
 * is offered too — but a picker that can only be clicked cannot be used from a keyboard, so the
 * numbers are the path and the click is the shortcut.
 *
 * ── ASK FOR THE SET YOU ARE USING, AND NOTHING ELSE ───────────────────────────────────────────
 * This used to pin every atom AND every bond, always, whatever the teacher was doing. Benzene
 * came out with twelve numbered discs on twelve positions and the molecule underneath them was
 * unreadable — *"an image that is uninterpretable because it's covered in truncated numbers"*.
 *
 * Only arrows need bond numbers; annotating needs atom numbers; naming and typing the SMILES
 * need none at all. So the caller says which, and the default is neither: on the page a numbered
 * molecule is noise, and that is the case a forgotten argument should fall into.
 *
 * `focus` marks the one atom being worked on, so a column of discs reads as "this one, and these
 * are the others" rather than as twelve equal claims on the eye.
 */
export function withIndexLabels(svg: string, opts?: {
  atoms?: boolean;
  bonds?: boolean;
  /** The atom index currently being edited, drawn as the live pin. */
  focus?: number;
}): string {
  const wantAtoms = opts?.atoms ?? false;
  const wantBonds = opts?.bonds ?? false;
  if (!wantAtoms && !wantBonds) return svg;

  const at = anchorPoints(svg);
  /* Sized from the molecule and not in fixed units — see `drawUnit`. */
  const r = drawUnit(at);

  /* Every disc's corners, so the box can grow to hold them — see `includeInViewBox`. */
  const edges: Point[] = [];
  const label = (p: Point, text: string, cls: string) => {
    edges.push({ x: p.x - r, y: p.y - r }, { x: p.x + r, y: p.y + r });
    return `<circle class="mx-pin-dot ${cls}" cx="${p.x}" cy="${p.y}" r="${r.toFixed(2)}"/>`
      + `<text class="mx-pin ${cls}" x="${p.x}" y="${(p.y + r * 0.37).toFixed(2)}"`
      + ` font-size="${(r * 1.15).toFixed(2)}" text-anchor="middle">${text}</text>`;
  };

  const pins = [
    ...(wantAtoms
      ? at.atoms.map((p, i) => (p
        ? label(p, String(i), i === opts?.focus ? 'mx-atom mx-focus' : 'mx-atom')
        : ''))
      : []),
    ...(wantBonds ? at.bonds.map((p, i) => (p ? label(p, String(i), 'mx-bond') : '')) : []),
  ].filter(Boolean).join('');

  if (!pins) return svg;
  const close = svg.lastIndexOf('</svg>');
  if (close < 0) return svg;
  const out = `${svg.slice(0, close)}<g class="mx-pins">${pins}</g>${svg.slice(close)}`;
  return includeInViewBox(out, edges);
}
